import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { get_questionnaires, bulkUploadThirdpartyQuestionsService } from "../../services";
import ButtonLoader from "../../Components/ButtonLoader";

function BulkUploadQuestionsModal({ show, onHide, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [resMsg, setResMsg] = useState("");
    const [errResMsg, setErrResMsg] = useState("");
    const [uploadFile, setUploadFile] = useState(null);

    // Questionnaire Name state
    const [questionnaireOptions, setQuestionnaireOptions] = useState([]);
    const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
    const [isCustomQuestionnaire, setIsCustomQuestionnaire] = useState(false);
    const [questionnaireName, setQuestionnaireName] = useState("");
    const [errors, setErrors] = useState({});

    const fileInputRef = useRef(null);

    // Fetch questionnaires when modal opens
    useEffect(() => {
        const fetchQuestionnaires = async () => {
            if (!show) return;
            setLoadingQuestionnaires(true);
            try {
                const response = await get_questionnaires();
                const data = response?.statusCode?.response_data?.data;
                if (data && data.questionnaires) {
                    const options = data.questionnaires.map(q => ({
                        value: q.name,
                        label: q.name
                    }));
                    options.push({ value: "custom", label: "Custom (Enter your own)" });
                    setQuestionnaireOptions(options);
                }
            } catch (error) {
                console.error("Error fetching questionnaires:", error);
            } finally {
                setLoadingQuestionnaires(false);
            }
        };

        if (show) {
            fetchQuestionnaires();
            // Reset state
            setResMsg("");
            setErrResMsg("");
            setUploadFile(null);
            setQuestionnaireName("");
            setIsCustomQuestionnaire(false);
            setErrors({});
        }
    }, [show]);

    const handleChange = (e) => {
        const { value } = e.target;

        // Handle questionnaire_name - distinguish between dropdown selection and text input
        if (isCustomQuestionnaire) {
            setQuestionnaireName(value);
            if (errors.questionnaireName) {
                setErrors(prev => ({ ...prev, questionnaireName: "" }));
            }
        } else {
            if (value === "custom") {
                setIsCustomQuestionnaire(true);
                setQuestionnaireName("");
            } else {
                setIsCustomQuestionnaire(false);
                setQuestionnaireName(value);
            }
            if (errors.questionnaireName) {
                setErrors(prev => ({ ...prev, questionnaireName: "" }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate custom questionnaire name for "Default"
        if (isCustomQuestionnaire) {
            if (questionnaireName && questionnaireName.trim().toLowerCase() === 'default') {
                newErrors.questionnaireName = "Questionnaire name cannot be 'Default'. Please choose a different name.";
            }
        }

        if (!uploadFile) {
            newErrors.file = "Please select a file to upload.";
        }

        return newErrors;
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setResMsg("");
        setErrResMsg("");

        try {
            // If "Default" is selected from dropdown, we can send "Default" or empty string (backend likely defaults to Default if empty)
            // But user might want to explicitly tag existing questionnaire.

            const response = await bulkUploadThirdpartyQuestionsService(uploadFile, questionnaireName);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message;
            const errorMessage =
                response?.errors?.message ||
                response?.error?.message ||
                response?.statusCode?.error?.message;

            if (successMessage && !errorMessage) {
                setResMsg(successMessage || "Questions uploaded successfully!");
                setUploadFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                setTimeout(() => {
                    onSuccess && onSuccess();
                    handleClose();
                }, 1500);
            } else if (errorMessage) {
                setErrResMsg(errorMessage || "Failed to upload questions. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading questions:", error);
            setErrResMsg("Failed to upload questions. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onHide();
    };

    return (
        <Modal className="cstm_modal" show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Bulk Upload Questions</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <Form onSubmit={handleUpload}>

                    {/* Questionnaire Name */}
                    <Form.Group className="mb-3">
                        <Form.Label className="font_500 color_white">Questionnaire Name (Optional)</Form.Label>
                        {isCustomQuestionnaire ? (
                            <div className="d-flex gap-2">
                                <div className="w-100">
                                    <Form.Control
                                        type="text"
                                        name="questionnaire_name"
                                        value={questionnaireName}
                                        onChange={handleChange}
                                        placeholder="e.g., Default, Cloud Security Assessment, SOC2 Audit"
                                        maxLength={200}
                                        isInvalid={!!errors.questionnaireName}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.questionnaireName}
                                    </Form.Control.Feedback>
                                </div>
                                {questionnaireOptions.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            setIsCustomQuestionnaire(false);
                                            setQuestionnaireName(questionnaireOptions.length > 0 ? questionnaireOptions[0].value : "");
                                        }}
                                        style={{ height: 'fit-content' }}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <Form.Select
                                    name="questionnaire_name"
                                    value={questionnaireName}
                                    onChange={handleChange}
                                    disabled={loadingQuestionnaires}
                                    isInvalid={!!errors.questionnaireName}
                                >
                                    <option value="">Select Questionnaire (Default)</option>
                                    {loadingQuestionnaires ? (
                                        <option value="" disabled>Loading questionnaires...</option>
                                    ) : (
                                        questionnaireOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))
                                    )}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.questionnaireName}
                                </Form.Control.Feedback>
                            </>
                        )}
                        <Form.Text className="text-white opacity-75">
                            Leave empty or select 'Default' to add to the Default questionnaire.
                        </Form.Text>
                    </Form.Group>

                    {/* File Upload */}
                    <Form.Group className="mb-4">
                        <Form.Label className="font_500 color_white">Upload File (Excel/CSV)</Form.Label>
                        <Form.Control
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={(e) => {
                                setUploadFile(e.target.files[0]);
                                if (errors.file) {
                                    setErrors(prev => ({ ...prev, file: "" }));
                                }
                            }}
                            isInvalid={!!errors.file}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.file}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Success Message */}
                    {resMsg && (
                        <div className="alert alert-success alert-dismissible fade show" role="alert">
                            <strong>Success!</strong> {resMsg}
                            <button type="button" className="btn-close" onClick={() => setResMsg('')}></button>
                        </div>
                    )}

                    {/* Error Message */}
                    {errResMsg && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            <strong>Error!</strong> {errResMsg}
                            <button type="button" className="btn-close" onClick={() => setErrResMsg('')}></button>
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <ButtonLoader /> : "Upload"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default BulkUploadQuestionsModal;
